import React, { useState, useEffect } from "react";
import { collection, addDoc, Timestamp, query, where, getDocs } from "firebase/firestore";
import { db } from "../service/firebase";
import {
  getAllStateNames,
  getDistrictsByState,
} from "../data/indiaStatesAndDistricts";
import "./OnboardingModal.css";

// SEGMENTS DATA - All jewellery business segments
const SEGMENTS = [
  { id: "gold", name: "Gold", icon: "🪙", color: "var(--gold-400, var(--brand-gold-400))" },
  { id: "silver", name: "Silver", icon: "⚪", color: "#cbd5e1" },
  { id: "platinum", name: "Platinum", icon: "🔷", color: "#7dd3fc" },
  { id: "diamond", name: "Diamond", icon: "💎", color: "#a5b4fc" },
  { id: "gem", name: "GEM", icon: "💠", color: "#5eead4" },
  { id: "pearl", name: "PEARL", icon: "⚪", color: "#fbcfe8" },
  { id: "bandhel", name: "BANDHEL", icon: "🔗", color: "#fcd34d" },
  { id: "decor", name: "DECOR", icon: "📦", color: "#d8b4fe" },
  { id: "brand", name: "BRAND", icon: "🏷️", color: "#fdba74" },
  { id: "tool", name: "TOOL", icon: "🔧", color: "#f59e0b" },
  { id: "machine", name: "MACHINE", icon: "⚙️", color: "#94a3b8" },
];

// Simple Earnings Calculator Component
const EarningsCalculator = ({ selectedSegment, selectedTurnover }) => {
  const [monthlyOrders, setMonthlyOrders] = useState(50);
  
  const calculateEarnings = () => {
    const turnoverMultiplier = {
      "₹10 Lakhs - ₹50 Lakhs": 0.02,
      "₹50 Lakhs - ₹2 Crore": 0.025,
      "₹2 Crore - ₹10 Crore": 0.03,
      "₹10 Crore+ (Institutional)": 0.035,
    };
    
    const segmentBonus = {
      gold: 1.2, diamond: 1.5, platinum: 1.3, silver: 0.9,
      gem: 1.4, pearl: 1.1, bandhel: 1.0, decor: 0.8,
      brand: 0.85, tool: 0.7, machine: 0.75,
    };
    
    const baseRate = turnoverMultiplier[selectedTurnover] || 0.025;
    const bonus = segmentBonus[selectedSegment?.id] || 1.0;
    
    let baseAmount = 50;
    if (selectedTurnover?.includes("10 Lakhs")) baseAmount = 30;
    else if (selectedTurnover?.includes("2 Crore")) baseAmount = 200;
    else if (selectedTurnover?.includes("10 Crore")) baseAmount = 500;
    
    const monthlyEarnings = baseAmount * baseRate * bonus * (monthlyOrders / 50);
    return Math.round(monthlyEarnings);
  };
  
  const earnings = calculateEarnings();
  
  return (
    <div className="earnings-calc-container">
      <div className="calc-header">
        <span className="calc-icon">🧮</span>
        <span className="calc-title">Estimated Monthly Earnings</span>
      </div>
      
      <div className="calc-segment-display">
        <span className="segment-badge" style={{ backgroundColor: selectedSegment?.color || '#d4af37' }}>
          {selectedSegment?.icon} {selectedSegment?.name || 'Select Segment'}
        </span>
      </div>
      
      <div className="calc-slider-section">
        <label>Monthly Orders: <strong>{monthlyOrders}</strong></label>
        <input 
          type="range" 
          min="10" 
          max="200" 
          value={monthlyOrders}
          onChange={(e) => setMonthlyOrders(parseInt(e.target.value))}
          className="calc-slider"
        />
      </div>
      
      <div className="calc-result">
        <div className="calc-amount">₹{earnings.toLocaleString()}</div>
        <div className="calc-period">per month</div>
      </div>
      
      <div className="calc-note">
        *Estimated earnings based on JMI network average. Actual may vary.
      </div>
    </div>
  );
};

const OnboardingModal = ({ isOpen, onClose, initialRole = "buyer", isModal = true }) => {
  const [role, setRole] = useState(initialRole);
  const allStates = getAllStateNames();
  const [showCalculator, setShowCalculator] = useState(false);

  const [formData, setFormData] = useState({
    businessName: "",
    contactPerson: "",
    email: "",
    phone: "",
    state: "Gujarat",
    district: "Surat (Diamond & Bullion Hub)",
    city: "",
    gstin: "",
    categoryInterest: "gold",
    estimatedTurnover: "₹50 Lakhs - ₹2 Crore",
    notes: "",
  });
  
  // Find selected segment from SEGMENTS based on categoryInterest
  const selectedSegment = SEGMENTS.find(s => s.id === formData.categoryInterest) || SEGMENTS[0];

  const [loading, setLoading] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [error, setError] = useState("");

  // Save form data to localStorage on change
  useEffect(() => {
    if (isOpen) {
      const modalKey = 'jmiOnboardingModal';
      const saved = localStorage.getItem(modalKey);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // Only restore if we have a fresh session (not submitted)
          if (!submittedId) {
            setFormData(prev => ({ ...prev, ...parsed }));
          }
        } catch (e) {
          console.error('Failed to parse saved onboarding data:', e);
          localStorage.removeItem(modalKey);
        }
      }
    }
  }, [isOpen, submittedId]);

  // Save form data to localStorage when it changes
  useEffect(() => {
    if (isOpen && Object.keys(formData).length > 0) {
      const modalKey = 'jmiOnboardingModal';
      localStorage.setItem(modalKey, JSON.stringify(formData));
    }
  }, [formData, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    const districts = getDistrictsByState(selectedState);
    setFormData((prev) => ({
      ...prev,
      state: selectedState,
      district: districts[0] || "",
    }));
    setError("");
  };

  const generateTrackingId = () => {
    const d = new Date();
    const dateStr = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
    const rand = Math.floor(1000 + Math.random() * 9000);
    return `JMI-REQ-${dateStr}-${rand}`;
  };

  const checkDuplicate = async (phone) => {
    try {
      const q = query(collection(db, "onboardingRequests"), where("phone", "==", phone));
      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch (err) {
      console.error("[OnboardingModal] Error checking duplicate:", err);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const cleanPhone = formData.phone.replace(/[^0-9]/g, "");
    if (cleanPhone.length < 10) {
      setError("Please provide a valid 10-digit mobile number.");
      return;
    }

    if (!formData.businessName.trim() || !formData.contactPerson.trim()) {
      setError("Business name and contact person are required.");
      return;
    }

    setLoading(true);

    // Check for duplicate
    const isDuplicate = await checkDuplicate(cleanPhone);
    if (isDuplicate) {
      setError("An application with this mobile number already exists. Your data has been saved. A JMI officer will contact you shortly.");
      setLoading(false);
      return;
    }

    const trackingId = generateTrackingId();

    try {
      await addDoc(collection(db, "onboardingRequests"), {
        trackingId,
        businessName: formData.businessName.trim(),
        contactPerson: formData.contactPerson.trim(),
        phone: cleanPhone,
        email: formData.email.trim(),
        city: formData.city.trim(),
        district: formData.district || "",
        state: formData.state,
        role:
          role === "seller"
            ? "seller"
            : role === "operator"
              ? "operator"
              : "buyer",
        gstin: formData.gstin.trim().toUpperCase(),
        categoryInterest: formData.categoryInterest,
        estimatedTurnover: formData.estimatedTurnover,
        notes: formData.notes.trim(),
        status: "pending",
        createdAt: Timestamp.now(),
        source: "landing_modal",
      });

      setSubmittedId(trackingId);
      // Clear localStorage on successful submission
      localStorage.removeItem('jmiOnboardingModal');
    } catch (err) {
      console.error("[OnboardingModal] Failed to submit application:", err);
      setSubmittedId(trackingId);
    } finally {
      setLoading(false);
    }
  };

  const handleResetAndClose = () => {
    setSubmittedId(null);
    setFormData({
      businessName: "",
      contactPerson: "",
      email: "",
      phone: "",
      state: "Gujarat",
      district: "Surat (Diamond & Bullion Hub)",
      city: "",
      gstin: "",
      categoryInterest: "gold",
      estimatedTurnover: "₹50 Lakhs - ₹2 Crore",
      notes: "",
    });
    setShowCalculator(false);
    if (isModal && onClose) onClose();
  };

  // Handle close for inline mode
  const handleInlineClose = () => {
    setSubmittedId(null);
    setShowCalculator(false);
  };

  // Render as inline form (no backdrop, no close button)
  if (!isModal) {
    return (
      <div className="onboarding-inline-form">
        {submittedId ? (
          <div className="auth-success-state">
            <div className="success-icon">✓</div>
            <div className="success-tracking-badge">
              <span>APPLICATION REF:</span>
              <strong>{submittedId}</strong>
            </div>
            <h3>Application Submitted!</h3>
            <p>Your application for <strong>{formData.businessName}</strong> has been registered.</p>
            <button type="button" className="btn btn-primary w-full" onClick={handleInlineClose}>
              Submit Another Application
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form-grid">
            <div className="auth-header-desc">
              <h4>
                {role === "seller"
                  ? "Apply for Seller & Manufacturer Access"
                  : role === "operator"
                    ? "Apply for JMI Regional Franchise / Operator"
                    : "Wholesale Jewellery Buyer Access Application"}
              </h4>
            </div>

            {error && <div className="auth-error-banner">⚠️ {error}</div>}

            <div className="input-group">
              <label>Business / Enterprise Legal Name *</label>
              <input
                type="text"
                name="businessName"
                placeholder="e.g. Mahalakshmi Jewellers Pvt Ltd"
                value={formData.businessName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="input-row-2">
              <div className="input-group">
                <label>Authorized Contact Person *</label>
                <input
                  type="text"
                  name="contactPerson"
                  placeholder="Your name"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="input-group">
                <label>Mobile Number *</label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="9876543210"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="input-row-2">
              <div className="input-group">
                <label>Business Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="jeweller@domain.com"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              <div className="input-group">
                <label>GSTIN</label>
                <input
                  type="text"
                  name="gstin"
                  placeholder="27AAAAA0000A1Z5"
                  value={formData.gstin}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="input-row-2">
              <div className="input-group">
                <label>State *</label>
                <select name="state" value={formData.state} onChange={handleStateChange} required>
                  {allStates.map((st) => <option key={st} value={st}>{st}</option>)}
                </select>
              </div>
              <div className="input-group">
                <label>District *</label>
                <select name="district" value={formData.district} onChange={handleChange} required>
                  {getDistrictsByState(formData.state).map((dist) => <option key={dist} value={dist}>{dist}</option>)}
                </select>
              </div>
            </div>

            <div className="input-group">
              <label>City / Market Hub</label>
              <input
                type="text"
                name="city"
                placeholder="e.g. Zaveri Bazaar"
                value={formData.city}
                onChange={handleChange}
              />
            </div>

            {/* Segment - Only for seller/operator, NOT for buyer */}
            {role !== "buyer" && (
              <div className="input-group">
                <label>Primary Jewellery Segment</label>
                <select name="categoryInterest" value={formData.categoryInterest} onChange={handleChange}>
                  <option value="gold">Gold (BIS 916 / 750 / 999)</option>
                  <option value="silver">Silver (BIS 925 Sterling & Artefacts)</option>
                  <option value="platinum">Platinum (Pt 950 Certified)</option>
                  <option value="diamond">Diamond (Natural & Lab-Grown Solitaires)</option>
                  <option value="gem">Gemstones</option>
                  <option value="pearl">Pearl</option>
                  <option value="bandhel">Bandhel</option>
                  <option value="decor">Decor</option>
                  <option value="brand">Brand & Packaging</option>
                  <option value="tool">Jewellery Tools</option>
                  <option value="machine">Jewellery Machinery</option>
                </select>
              </div>
            )}

            <div className={role === "buyer" ? "input-row-2" : "input-group"}>
              <div className="input-group">
                <label>Estimated Monthly Sourcing Volume</label>
                <select name="estimatedTurnover" value={formData.estimatedTurnover} onChange={handleChange}>
                  <option value="₹10 Lakhs - ₹50 Lakhs">₹10 Lakhs - ₹50 Lakhs</option>
                  <option value="₹50 Lakhs - ₹2 Crore">₹50 Lakhs - ₹2 Crore</option>
                  <option value="₹2 Crore - ₹10 Crore">₹2 Crore - ₹10 Crore</option>
                  <option value="₹10 Crore+ (Institutional)">₹10 Crore+ (Enterprise)</option>
                </select>
              </div>
              
              {role === "buyer" && (
                <div className="input-group">
                  <label>Calculate Potential Earnings</label>
                  <button type="button" className="calc-toggle-btn" onClick={() => setShowCalculator(!showCalculator)}>
                    <span>🧮</span>
                    {showCalculator ? "Hide Calculator" : "Calculate Earnings"}
                  </button>
                </div>
              )}
            </div>

            {role === "buyer" && showCalculator && (
              <EarningsCalculator selectedSegment={selectedSegment} selectedTurnover={formData.estimatedTurnover} />
            )}

            <button type="submit" disabled={loading} className={`btn ${role === "seller" ? "btn-outline-gold" : "btn-primary"} w-full auth-submit-btn`}>
              {loading ? "Submitting..." : role === "seller" ? "Submit Manufacturer Application →" : role === "operator" ? "Submit Operator Application →" : "Submit Wholesale Buyer Application →"}
            </button>
          </form>
        )}
      </div>
    );
  }

  // Modal mode (original)
  return (
    <div className="auth-modal-backdrop" onClick={handleResetAndClose}>
      <div className="auth-modal-card" onClick={(e) => e.stopPropagation()}>
        <button className="auth-modal-close" onClick={handleResetAndClose} aria-label="Close modal">✕</button>

{submittedId ? (
           <div className="auth-success-state">
             <div className="success-icon">✓</div>
             <div className="success-tracking-badge">
               <span>APPLICATION REF:</span>
               <strong>{submittedId}</strong>
             </div>
             <h3>Onboarding Request Submitted!</h3>
             <p>
               Your verified access application for{" "}
               <strong>{formData.businessName}</strong> has been registered with
               JMI Central Governance.
             </p>
             <div className="success-instruction-card">
               <div className="inst-title">What happens next?</div>
               <ol className="inst-list">
                 <li>
                   JMI SuperAdmin & Operations will review your business
                   credentials.
                 </li>
                 <li>
                   An authorized JMI representative will contact{" "}
                   <strong>+91 {formData.phone}</strong> within 2 business hours.
                 </li>
                 <li>
                   Upon verification, your app login credentials will be
                   dispatched via SMS and WhatsApp.
                 </li>
               </ol>
             </div>
             <div className="officer-note">
               📞 A JMI onboarding officer will reach out to you quickly for any assistance.
             </div>
             <button
               type="button"
               className="btn btn-primary w-full"
               onClick={handleResetAndClose}
             >
               Done & Return to Homepage
             </button>
           </div>
        ) : (
          <>
            {/* Modal Brand Header */}
            <div className="auth-modal-brand-header">
              <img src="/logo.png" alt="JMI Logo" className="modal-logo-img" />
              <div className="modal-brand-text">
                <span className="modal-brand-title">JEWEL MART INDIA</span>
                <span className="modal-brand-sub">B2B PARTNER NETWORK</span>
              </div>
            </div>

            {/* Header / Tabs */}
            <div className="auth-tabs-row">
              <button
                type="button"
                className={`auth-tab-btn ${role === "buyer" ? "active buyer" : ""}`}
                onClick={() => setRole("buyer")}
              >
                Retailer / Buyer
              </button>
              <button
                type="button"
                className={`auth-tab-btn ${role === "seller" ? "active seller" : ""}`}
                onClick={() => setRole("seller")}
              >
                Manufacturer / Seller
              </button>
              <button
                type="button"
                className={`auth-tab-btn ${role === "operator" ? "active operator" : ""}`}
                onClick={() => setRole("operator")}
              >
                Regional Operator
              </button>
            </div>

            <div className="auth-header-desc">
              <h4>
                {role === "seller"
                  ? "Apply for Seller & Manufacturer Access"
                  : role === "operator"
                    ? "Apply for JMI Regional Franchise / Operator"
                    : "Wholesale Jewellery Buyer Access Application"}
              </h4>
              <p>
                {role === "seller"
                  ? "Direct listing to India’s verified jeweller retail network with locked bullion rates & guaranteed settlements."
                  : role === "operator"
                    ? "Manage regional logistics, delivery hub, vault custody, and verified jeweller operations."
                    : "Gain direct wholesale access to verified BIS hallmarked manufacturers with zero counterparty risk."}
              </p>
            </div>

            {error && <div className="auth-error-banner">⚠️ {error}</div>}

            <form onSubmit={handleSubmit} className="auth-form-grid">
              <div className="input-group">
                <label>Business / Enterprise Legal Name *</label>
                <input
                  type="text"
                  name="businessName"
                  placeholder="e.g. Mahalakshmi Jewellers Pvt Ltd"
                  value={formData.businessName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="input-row-2">
                <div className="input-group">
                  <label>Authorized Contact Person *</label>
                  <input
                    type="text"
                    name="contactPerson"
                    placeholder="e.g. Your name  (Partner/Owner)"
                    value={formData.contactPerson}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="input-group">
                  <label>Mobile Number (For App Login & OTP) *</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="e.g. 9876543210"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="input-row-2">
                <div className="input-group">
                  <label>Business Email</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="jeweller@domain.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                <div className="input-group">
                  <label>GSTIN (Optional for initial review)</label>
                  <input
                    type="text"
                    name="gstin"
                    placeholder="27AAAAA0000A1Z5"
                    value={formData.gstin}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="input-row-2">
                <div className="input-group">
                  <label>State / Union Territory *</label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleStateChange}
                    required
                  >
                    {allStates.map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="input-group">
                  <label>District *</label>
                  <select
                    name="district"
                    value={formData.district}
                    onChange={handleChange}
                    required
                  >
                    {getDistrictsByState(formData.state).map((dist) => (
                      <option key={dist} value={dist}>
                        {dist}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="input-group">
                <label>City / Market Hub / Showroom Area</label>
                <input
                  type="text"
                  name="city"
                  placeholder="e.g. Zaveri Bazaar / Varachha / Karol Bagh / Market Road"
                  value={formData.city}
                  onChange={handleChange}
                />
              </div>

              {/* Primary Jewellery Segment - Only for Seller/Operator, NOT for Buyer */}
              {role !== "buyer" && (
                <div className="input-group">
                  <label>Primary Jewellery Segment</label>
                  <select
                    name="categoryInterest"
                    value={formData.categoryInterest}
                    onChange={handleChange}
                  >
                    <option value="gold">Gold (BIS 916 / 750 / 999)</option>
                    <option value="silver">Silver (BIS 925 Sterling & Artefacts)</option>
                    <option value="platinum">Platinum (Pt 950 Certified)</option>
                    <option value="diamond">Diamond (Natural & Lab-Grown Solitaires)</option>
                    <option value="gem">Gemstones (Precious & Semi-Precious)</option>
                    <option value="pearl">Pearl (Freshwater, South Sea & Basra)</option>
                    <option value="bandhel">Bandhel (Heritage Craft & Wirework)</option>
                    <option value="decor">Decor (Showroom Displays & Trays)</option>
                    <option value="brand">Brand & Jewellery Packaging</option>
                    <option value="tool">Jewellery Tools (Bench & Assaying)</option>
                    <option value="machine">Jewellery Machinery (CNC & Laser)</option>
                  </select>
                </div>
              )}

              {/* Buyer gets turnover + calculator, seller/operator only turnover dropdown */}
              <div className={role === "buyer" ? "input-row-2" : "input-group"}>
                <div className="input-group">
                  <label>Estimated Monthly Sourcing Volume</label>
                  <select
                    name="estimatedTurnover"
                    value={formData.estimatedTurnover}
                    onChange={handleChange}
                  >
                    <option value="₹10 Lakhs - ₹50 Lakhs">
                      ₹10 Lakhs - ₹50 Lakhs
                    </option>
                    <option value="₹50 Lakhs - ₹2 Crore">
                      ₹50 Lakhs - ₹2 Crore
                    </option>
                    <option value="₹2 Crore - ₹10 Crore">
                      ₹2 Crore - ₹10 Crore
                    </option>
                    <option value="₹10 Crore+ (Institutional)">
                      ₹10 Crore+ (Enterprise)
                    </option>
                  </select>
                </div>
                
                {role === "buyer" && (
                  <div className="input-group">
                    <label>Calculate Potential Earnings</label>
                    <button
                      type="button"
                      className="calc-toggle-btn"
                      onClick={() => setShowCalculator(!showCalculator)}
                    >
                      <span>🧮</span>
                      {showCalculator ? "Hide Calculator" : "Calculate Earnings"}
                    </button>
                  </div>
                )}
              </div>

              {/* Earnings Calculator - Only for buyer */}
              {role === "buyer" && showCalculator && (
                <EarningsCalculator 
                  selectedSegment={selectedSegment} 
                  selectedTurnover={formData.estimatedTurnover}
                />
              )}

              <button
                type="submit"
                disabled={loading}
                className={`btn ${role === "seller" ? "btn-outline-gold" : "btn-primary"} w-full auth-submit-btn`}
              >
                {loading
                  ? "Submitting Application..."
                  : role === "seller"
                    ? "Submit Manufacturer Application →"
                    : role === "operator"
                      ? "Submit Operator Application →"
                      : "Submit Wholesale Buyer Application →"}
              </button>

              <div className="verification-guarantee-note">
                🔒 Direct SuperAdmin Onboarding Gateway. No self-registration
                bypass allowed for security.
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default OnboardingModal;
